import { Component, computed, inject, input, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { selectUser } from '@core/store/auth/auth.selectors';
import { selectPlanEditState } from '@core/store/plan-edit/plan-edit.selectors';
import { HOLDER_KIND_KEYS, planHolderView } from '@core/store/plan-edit/plan-holder-view';

import { ButtonComponent } from '@shared/components/button/button.component';

@Component({
  selector: 'app-plan-edit-banner',
  imports: [ButtonComponent, TranslatePipe],
  templateUrl: './plan-edit-banner.component.html',
  styles: ``
})
export class PlanEditBannerComponent {
  readonly planId = input<string | null>(null);
  /** The flavour of the data view showing the banner; null on the plan page. */
  readonly flavour = input<string | null>(null);

  private readonly store = inject(Store);
  private readonly assistant = inject(AssistantService);
  private readonly notifications = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly planEdit = this.store.selectSignal(selectPlanEditState);
  private readonly user = this.store.selectSignal(selectUser);

  protected readonly ICONS = ICONS;
  /** The tab editing the plan shows its edit bar here instead. */
  protected readonly view = computed(() => {
    const view = planHolderView(
      this.planEdit(),
      this.user()?.id,
      this.planId(),
      this.flavour()
    );
    return view?.kind === 'own-here' ? null : view;
  });
  protected readonly textKey = computed(() => {
    const view = this.view();
    return view
      ? `features.plan.edit.banner.${HOLDER_KIND_KEYS[view.kind]}.${view.live ? 'live' : 'static'}`
      : '';
  });
  protected readonly discarding = signal(false);

  protected openChat(): void {
    const hold = this.view()?.assistantHold;
    if (hold) this.assistant.openChatTab(hold.tabId);
  }

  protected async discard(): Promise<void> {
    const hold = this.view()?.assistantHold;
    if (!hold || this.discarding()) return;
    this.discarding.set(true);
    try {
      if (!(await this.assistant.discardPlanEdit(hold.tabId, hold.editSessionId)))
        this.notifications.error(
          this.translate.instant('features.plan.edit.banner.discard'),
          this.translate.instant('features.plan.edit.banner.discard_failed')
        );
    } finally {
      this.discarding.set(false);
    }
  }
}
